import { useEffect, useState } from 'react';
import { api, networkWords } from '../api.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { reinviteWarning } from '../share-invite.js';

// Per-tank sharing panel. Owner-only — viewers and editors get a quiet
// "you have access to this tank, but only the owner can manage who else
// does" line instead. Mirrors HydrosLinkPanel + DevicesPanel for
// visual consistency.
//
// Capabilities:
//   - List active grants (pending invites + accepted users)
//   - Invite by email with role picker (viewer | editor)
//   - Revoke
//   - Transfer ownership (only to an existing accepted grantee)
//
// Audit drawer is deferred — owners can hit GET /api/tanks/:id/audit
// directly for now. The most painful gap was the sharing form itself.

export function SharingPanel({ tank, onReload }) {
  const confirm = useConfirm();
  const isOwner = tank.role === 'owner';
  const [grants, setGrants] = useState(null);
  const [err, setErr] = useState('');
  // A failed revoke or alert switch is said under that person's row: on a
  // long list the top line was screens away.
  const [errAt, setErrAt] = useState(null);
  const [busy, setBusy] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [inviteErr, setInviteErr] = useState('');
  const [manualLink, setManualLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [mine, setMine] = useState(null);
  // 'loading' | 'ok' | 'failed'. A failed load keeps the people already on
  // screen and says it could not load, never "0 people" or "You're the only
  // person on this tank" over a partner it could not see.
  const [listState, setListState] = useState('loading');

  const reload = async ({ quiet = false } = {}) => {
    if (!isOwner) {
      setGrants([]);
      // An editor whose alerts the owner switched on is told so; their own
      // account-wide settings were the only place it showed before.
      if (tank.role === 'editor') {
        try {
          const r = await api(`/api/tanks/${tank.id}/shares`);
          setMine(r.mine || null);
        } catch {
          setMine(null);
        }
      }
      return;
    }
    try {
      const r = await api(`/api/tanks/${tank.id}/shares`);
      setGrants(r.grants || []);
      setListState('ok');
    } catch (e) {
      // After a send that went through, its own line says so: a list that
      // did not refresh is not an error on top of it.
      if (quiet) return;
      setListState('failed');
      setErrAt(null);
      setErr(networkWords(e) || e.message || 'Could not load who has access.');
    }
  };

  useEffect(() => {
    reload();
  }, [tank.id]);

  // Non-owners see a stripped-down panel acknowledging their role.
  if (!isOwner) {
    const roleLabel = tank.role === 'editor' ? 'editor' : 'viewer';
    return (
      <div className="ts-add-form" style={{ marginBottom: 14 }}>
        <div className="ts-add-head">
          <h4 className="font-heading">Sharing</h4>
          <span className="ts-hint">your role on this tank</span>
        </div>
        <p className="ts-hint" style={{ marginTop: 8 }}>
          You have <strong>{roleLabel}</strong> access to {tank.name}.{' '}
          {tank.role === 'viewer'
            ? "You can see everything but you can't add or change anything."
            : "You can add and change daily data (readings, doses, livestock, equipment) but you can't manage who else has access or change the tank's profile. The owner can choose to send you this tank's alerts; they follow your own notification settings."}{' '}
          Only the tank owner can grant or revoke access.
        </p>
        {tank.role === 'editor' && mine?.alert_editor === true && (
          <p className="ts-hint" style={{ marginTop: 8 }}>
            The owner sends you this tank&rsquo;s alerts. To stop them for this tank, ask the owner:
            turning alerts off in Account &gt; Notifications stops them for your own tanks too.
          </p>
        )}
      </div>
    );
  }

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      setInviteErr('Enter an email address.');
      return;
    }
    setBusy('invite');
    setInviteErr('');
    setStatusMsg(null);
    setManualLink(null);
    setCopied(false);
    const send = (confirmReinvite) =>
      api(`/api/tanks/${tank.id}/shares`, {
        method: 'POST',
        body: { email, role: inviteRole, ...(confirmReinvite ? { confirm_reinvite: true } : {}) },
      });
    try {
      let r;
      try {
        r = await send(false);
      } catch (e) {
        // The server asks before re-inviting someone who has joined, from its
        // own list: the one on screen can be stale.
        if (e.status !== 409 || e.payload?.error !== 'already_has_access') throw e;
        const ok = await confirm(
          reinviteWarning({
            email,
            tankName: tank.name,
            role: e.payload.role,
            alerted: e.payload.role === 'editor' && e.payload.alert_editor === true,
            inviteRole,
          })
        );
        if (!ok) return;
        r = await send(true);
      }
      setInviteEmail('');
      const sent = r.email_status === 'sent';
      setStatusMsg(
        sent
          ? `Invite sent to ${email}.`
          : r.accept_url
            ? `The invite is ready, but the email to ${email} did not go out. Send them this link yourself:`
            : `The invite is ready, but the email to ${email} did not go out. Try inviting again in a minute.`
      );
      setManualLink(sent ? null : r.accept_url || null);
      await reload({ quiet: true });
    } catch (e) {
      const code = e.payload?.error || e.message;
      setInviteErr(networkWords(e) || humanError(code) || code || 'Could not send the invite.');
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (grant) => {
    const label = grant.grantee_email;
    const ok = await confirm({
      title: `Revoke access for ${label}?`,
      body: grant.accepted_at
        ? `${label} will lose access to ${tank.name} immediately. They keep nothing — readings they logged stay, but they can't see them through this tank anymore.`
        : `The pending invite to ${label} will be cancelled. The accept link stops working.`,
      confirmLabel: 'Revoke',
      destructive: true,
    });
    if (!ok) return;
    setBusy(grant.id);
    setErr('');
    setErrAt(null);
    try {
      await api(`/api/tanks/${tank.id}/shares/${grant.id}`, { method: 'DELETE' });
      await reload();
    } catch (e) {
      setErrAt(grant.id);
      setErr(networkWords(e) || e.message || 'Could not revoke.');
    } finally {
      setBusy(null);
    }
  };

  // The owner's "also alert this person" switch (editors only, off until
  // the owner turns it on). Their own notification settings decide how and
  // when it reaches them.
  const setAlerts = async (grant, on) => {
    setBusy(`alerts:${grant.id}`);
    setErr('');
    setErrAt(null);
    try {
      await api(`/api/tanks/${tank.id}/shares/${grant.id}/alerts`, {
        method: 'PATCH',
        body: { on },
      });
      await reload();
    } catch (e) {
      setErrAt(grant.id);
      setErr(
        networkWords(e) ||
          (e.payload?.error === 'not_an_editor'
            ? 'Alerts can only go to an editor who has joined the tank.'
            : e.message || 'Could not change their alerts.')
      );
    } finally {
      setBusy(null);
    }
  };

  const transfer = async () => {
    const email = transferEmail.trim().toLowerCase();
    if (!email) {
      setErr("Enter the new owner's email.");
      return;
    }
    const ok = await confirm({
      title: `Transfer ${tank.name} to ${email}?`,
      body: `${email} becomes the new owner. You become an editor — you can still log readings and changes, but they control sharing, deletion, and the tank profile. Alerts you switched on for editors are switched off; the new owner decides who gets them. This cannot be undone from your side; the new owner has to transfer it back.`,
      confirmLabel: 'Transfer ownership',
      destructive: true,
    });
    if (!ok) return;
    setBusy('transfer');
    setErr('');
    setErrAt(null);
    try {
      await api(`/api/tanks/${tank.id}/owner`, { method: 'PATCH', body: { email } });
      setStatusMsg(`Ownership transferred to ${email}. You're an editor on this tank now.`);
      setShowTransfer(false);
      setTransferEmail('');
      await onReload?.();
      await reload();
    } catch (e) {
      const code = e.payload?.error || e.message;
      setErrAt('transfer');
      setErr(networkWords(e) || humanError(code) || code || 'Could not transfer ownership.');
    } finally {
      setBusy(null);
    }
  };

  const activeAccepted = (grants || []).filter((g) => g.accepted_at);
  const pending = (grants || []).filter((g) => !g.accepted_at);

  return (
    <div className="ts-add-form" style={{ marginBottom: 14 }}>
      <div className="ts-add-head">
        <h4 className="font-heading">Sharing</h4>
        <span className="ts-hint">
          {listState === 'failed'
            ? 'Could not load who has access'
            : grants === null
              ? 'Loading…'
              : `${activeAccepted.length} ${activeAccepted.length === 1 ? 'person' : 'people'} can access this tank${pending.length ? ` · ${pending.length} pending` : ''}`}
        </span>
      </div>

      {err && !errAt && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      {listState === 'ok' && grants?.length === 0 && (
        <p className="ts-hint" style={{ marginTop: 8 }}>
          You're the only person on this tank. Invite a tank partner, a maintenance helper, or a
          friend: they get an email with a link to join.
        </p>
      )}
      {/* The form is open on the page, not behind "Invite someone" (owner,
          2026-10-23: three taps at most; inviting took four), and above the
          list, which can run a few screens for a maintenance company. */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--surface-border)' }}>
        <h4 className="font-heading" style={{ margin: '0 0 8px', fontSize: 16 }}>
          Invite someone
        </h4>
        <label className="ts-field">
          <span>Their email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="none"
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              setInviteErr('');
            }}
            placeholder="reefer@example.com"
            style={{ minHeight: 44 }}
          />
        </label>
        <label className="ts-field" style={{ marginTop: 8 }}>
          <span>Access level</span>
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            style={{ minHeight: 44 }}
          >
            <option value="viewer">Viewer — read-only</option>
            <option value="editor">Editor — can log daily data</option>
          </select>
        </label>
        <p className="ts-hint" style={{ marginTop: 6 }}>
          {inviteRole === 'editor'
            ? "Editors can write daily data but can't delete the tank, change its profile, or manage sharing. Once they join, you can choose to send them this tank's alerts too."
            : 'Viewers can see everything but never write, and never get alerts.'}
        </p>
        <button
          type="button"
          className="ts-save"
          style={{ marginTop: 10, minHeight: 44 }}
          onClick={invite}
          disabled={busy === 'invite' || !inviteEmail.trim()}
        >
          {busy === 'invite' ? 'Sending…' : 'Send invite'}
        </button>
        {inviteErr && (
          <div role="alert" className="ts-err" style={{ marginTop: 8 }}>
            {inviteErr}
          </div>
        )}
        {/* Said beside the button: a long list put the old top line off screen. */}
        {statusMsg && (
          <p className="ts-hint" style={{ marginTop: 8, color: 'var(--cyan)' }}>
            {statusMsg}
          </p>
        )}
        {manualLink && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <input
              readOnly
              value={manualLink}
              aria-label="Invite link"
              onFocus={(e) => e.target.select()}
              style={{ flex: '1 1 220px', minWidth: 0, minHeight: 44 }}
            />
            <button
              type="button"
              className="ts-chip"
              style={{ minHeight: 44 }}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(manualLink);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch {
                  /* the field is selectable as a fallback */
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button
                type="button"
                className="ts-chip"
                style={{ minHeight: 44 }}
                onClick={() =>
                  navigator
                    .share({ title: `Join ${tank.name} on TankSensei`, url: manualLink })
                    .catch(() => {})
                }
              >
                Share
              </button>
            )}
          </div>
        )}
      </div>

      {grants === null ? (
        listState === 'loading' && (
          <p className="ts-hint" style={{ marginTop: 8 }}>
            Loading…
          </p>
        )
      ) : grants.length === 0 ? null : (
        <ul
          className="ts-device-list"
          aria-label="Who has access"
          style={{ listStyle: 'none', padding: 0, margin: '16px 0 8px' }}
        >
          {grants.map((g) => (
            <li
              key={g.id}
              className="ts-row-body"
              style={{
                alignItems: 'center',
                padding: '8px 0',
                borderTop: '1px solid var(--border-soft, rgba(0,0,0,0.08))',
              }}
            >
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <strong style={{ overflowWrap: 'anywhere' }}>{g.grantee_email}</strong>
                <p className="ts-hint" style={{ margin: '2px 0 0' }}>
                  {roleLabel(g.role)}
                  {' · '}
                  {g.accepted_at
                    ? `joined ${formatAgo(g.accepted_at)}`
                    : `invited ${formatAgo(g.granted_at)} · pending`}
                </p>
                {g.role === 'editor' && g.accepted_at && (
                  <label
                    className="ts-share-alerts"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      minHeight: 44,
                      marginTop: 4,
                      cursor: busy === `alerts:${g.id}` ? 'wait' : 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={g.alert_editor === true}
                      disabled={busy === `alerts:${g.id}`}
                      onChange={(e) => setAlerts(g, e.target.checked)}
                      style={{ width: 22, height: 22, flex: '0 0 auto' }}
                    />
                    <span>
                      <span style={{ fontSize: 15, color: 'var(--text)' }}>
                        Also send them this tank's alerts
                      </span>
                      <span className="ts-hint" style={{ display: 'block' }}>
                        Readings out of range, a display offline (power loss) and a stopped dose
                        head, by their own notification settings. You still get yours.
                      </span>
                    </span>
                  </label>
                )}
              </div>
              <button
                type="button"
                className="ts-chip ts-chip-danger"
                onClick={() => revoke(g)}
                disabled={busy === g.id}
              >
                {busy === g.id ? '…' : g.accepted_at ? 'Revoke' : 'Cancel invite'}
              </button>
              {err && errAt === g.id && (
                <div role="alert" className="ts-err" style={{ flexBasis: '100%', marginTop: 6 }}>
                  {err}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Ownership transfer — collapsed by default. Only enabled when
          there's at least one accepted grantee (you can only transfer
          to someone who's already accepted access). */}
      {activeAccepted.length > 0 && (
        <div
          style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--surface-border)' }}
        >
          {!showTransfer ? (
            <button type="button" className="ts-chip" onClick={() => setShowTransfer(true)}>
              Transfer ownership →
            </button>
          ) : (
            <div>
              <p className="ts-hint" style={{ marginBottom: 6 }}>
                Transfer this tank to one of the people who already has access. You become an
                editor. They become the owner.
              </p>
              <label className="ts-field">
                <span>New owner's email</span>
                <select value={transferEmail} onChange={(e) => setTransferEmail(e.target.value)}>
                  <option value="">Pick someone…</option>
                  {activeAccepted.map((g) => (
                    <option key={g.id} value={g.grantee_email}>
                      {g.grantee_email} ({roleLabel(g.role)})
                    </option>
                  ))}
                </select>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                <button
                  type="button"
                  className="ts-chip ts-chip-danger"
                  onClick={transfer}
                  disabled={busy === 'transfer' || !transferEmail}
                >
                  {busy === 'transfer' ? 'Transferring…' : 'Transfer'}
                </button>
                {err && errAt === 'transfer' && (
                  <div role="alert" className="ts-err" style={{ flexBasis: '100%' }}>
                    {err}
                  </div>
                )}
                <button
                  type="button"
                  className="ts-chip"
                  onClick={() => {
                    setShowTransfer(false);
                    setTransferEmail('');
                    setErr('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function roleLabel(role) {
  if (role === 'owner') return 'Owner';
  if (role === 'editor') return 'Editor (can write)';
  return 'Viewer (read-only)';
}

function formatAgo(ts) {
  if (!ts) return 'never';
  const s = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function humanError(code) {
  if (code === 'cannot_share_with_self') return "That's your own email.";
  if (code === 'invalid_email') return "That doesn't look like an email address.";
  if (code === 'not_an_active_grantee')
    return "You can only transfer ownership to someone who's already accepted an invite to this tank. Invite them first.";
  if (code === 'cannot_transfer_to_self') return "That's you — you're already the owner.";
  if (code === 'owner_only') return 'Only the tank owner can do that.';
  return null;
}
